import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import './Page9.css';

function Page9() {
  const [isRecording, setIsRecording] = useState(false);
  const [audioChunks, setAudioChunks] = useState([]);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioText, setAudioText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const mediaRecorder = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const isRecordingRef = useRef(false);

  const startRecording = useCallback(async () => {
    if (isRecordingRef.current) return; // Prevent multiple starts
    
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('getUserMedia is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
          console.log("Event Data Size:", event.data.size);
        }
      };

      mediaRecorder.current.onstop = () => {
        console.log("Audio Chunks (onstop before Blob):", chunks);
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        console.log("Audio Blob Size:", audioBlob.size);
        sendAudio(audioBlob);
        setAudioChunks([]);
      };

      mediaRecorder.current.start();
      console.log("MediaRecorder State (Start):", mediaRecorder.current.state);
      isRecordingRef.current = true;
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (!isRecordingRef.current) return; // Prevent multiple stops
    
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
      console.log("MediaRecorder State (Stop):", mediaRecorder.current.state);
      isRecordingRef.current = false;
      setIsRecording(false);
      if (mediaRecorder.current.stream) {
        mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      }
    }
    
    // Clear any pending timeout
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  }, []);

  // Handle touch start with proper mobile support
  const handleTouchStart = useCallback((e) => {
    e.preventDefault(); // Prevent context menu and text selection
    e.stopPropagation();
    
    // Add a small delay to prevent accidental triggers
    recordingTimeoutRef.current = setTimeout(() => {
      startRecording();
    }, 100);
  }, [startRecording]);

  // Handle touch end
  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear the timeout if touch ended before delay
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    
    stopRecording();
  }, [stopRecording]);

  // Handle mouse events for desktop
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    startRecording();
  }, [startRecording]);

  const handleMouseUp = useCallback((e) => {
    e.preventDefault();
    stopRecording();
  }, [stopRecording]);

  // Handle mouse leave to stop recording if user drags away
  const handleMouseLeave = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const sendAudio = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recorded_audio.webm');
      formData.append('tp', 'upload_call');
      
      const response = await axios.post('https://kidr78home.duckdns.org/api/upload_audio', formData, {
        headers: {
          'Authorization': 'kSuIlUmNaTiIeO5',
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        console.log('Audio sent successfully!');
      } else {
        console.error('Failed to send audio:', response.data);
      }
    } catch (error) {
      console.error('Error sending audio:', error);
    }
  };

  const fetchAudio = async () => {
    setIsLoading(true);
    try {
      const filename = 'recorded_audio.webm';
      const response = await axios.get(`https://kidr78home.duckdns.org/api/get_audio/${filename}`, {
        responseType: 'blob',
        headers: {
           'Authorization': 'kSuIlUmNaTiIeO5',
           'Content-Type': 'multipart/form-data',
         },
      });

      const blob = new Blob([response.data], { type: 'audio/webm' });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);

      await fetchText();
    } catch (error) {
      console.error('Error fetching audio:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchText = async () => {
    try {
      const filename = 'recorded_audio.webm';
      const response = await axios.get(`https://kidr78home.duckdns.org/api/audio_text/${filename}`, {
        headers: {
           'Authorization': 'kSuIlUmNaTiIeO5',
           'Content-Type': 'multipart/form-data',
         },
      });
      
      const jsonData = response.data;
      if (jsonData && jsonData.resp) {
          setAudioText(jsonData.resp);
          console.log("The string value is:", jsonData.resp);
       } else {
          console.error("Invalid JSON response or 'resp' key not found");
       }
   } catch (error) {
       console.error('Error fetching text:', error);
   }
  };

  return (
    <div className="audio-container">
      <div className="audio-header">
        <h1 className="audio-title">🎤 Voice Recorder</h1>
        <p className="audio-subtitle">Hold the button to record your voice</p>
      </div>

      <div className="recording-section">
        {/* Large Round Record Button */}
        <div className="record-button-container">
          <button
            className={`record-button ${isRecording ? 'recording' : ''}`}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            onContextMenu={(e) => e.preventDefault()} // Prevent context menu
            style={{ 
              touchAction: 'none', // Prevent scrolling and zooming
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none' // Prevent iOS callout
            }}
          >
            <div className="record-button-inner">
              {isRecording ? (
                <>
                  <div className="recording-pulse"></div>
                  <span className="record-icon recording-icon">⏹</span>
                  <span className="record-text">Recording...</span>
                </>
              ) : (
                <>
                  <span className="record-icon">🎤</span>
                  <span className="record-text">Hold to Record</span>
                </>
              )}
            </div>
          </button>
        </div>

        {/* Recording Status */}
        {isRecording && (
          <div className="recording-status">
            <div className="recording-indicator">
              <div className="pulse-dot"></div>
              <span>Recording in progress...</span>
            </div>
          </div>
        )}
      </div>

      {/* Control Buttons */}
      <div className="control-section">
        <button 
          className={`load-button ${isLoading ? 'loading' : ''}`}
          onClick={fetchAudio} 
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="loading-spinner"></span>
              Loading...
            </>
          ) : (
            <>
              <span className="load-icon">📥</span>
              Load Last Recording
            </>
          )}
        </button>
      </div>

      {/* Audio Player */}
      {audioUrl && (
        <div className="audio-player-section">
          <h3 className="section-title">🔊 Playback</h3>
          <audio 
            controls 
            src={audioUrl} 
            onEnded={() => URL.revokeObjectURL(audioUrl)}
            className="audio-player"
          >
            Your browser does not support the audio element.
          </audio>
        </div>
      )}

      {/* Transcription */}
      {audioText && (
        <div className="transcription-section">
          <h3 className="section-title">📝 Transcription</h3>
          <div className="transcription-card">
            <p className="transcription-text">{audioText}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Page9;